import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import DeleteIcon from 'assets/settings/components/DeleteIcon';
import EditIcon from 'assets/settings/components/EditIcon';
import Select from 'common-ui/select/Select';
import Pagination from 'common-ui/table/Pagination';
import {
  getAdministrativeDistricts,
  getAdministrativeRegions,
  getCities,
} from 'features/newsales/newsalesActions';
import {
  selectAdministrativeDistricts,
  selectAdministrativeDistrictsCurrentPage,
  selectAdministrativeDistrictsDeleteSuccess,
  selectAdministrativeDistrictsLoading,
  selectAdministrativeDistrictsPagesCount,
  selectAdministrativeDistrictsSearchData,
  selectAdministrativeDistrictsSelectedCityId,
  selectAdministrativeDistrictsSelectedCountryId,
  selectAdministrativeDistrictsSelectedRegionId,
  selectAdministrativeDistrictsSuccess,
  selectAdministrativeDistrictsUpdateSuccess,
  setAdministrativeDistrictsCreateSuccess,
  setAdministrativeDistrictsCurrentPage,
  setAdministrativeDistrictsDeleteSuccess,
  setAdministrativeDistrictsSearchData,
  setAdministrativeDistrictsSelectedCity,
  setAdministrativeDistrictsSelectedCountry,
  setAdministrativeDistrictsSelectedRegion,
  setAdministrativeDistrictsUpdateSuccess,
} from 'features/newsales/newsalesSlice';

import {
  ActionsCell,
  EmptyState,
  FilterFlex,
  FilterSection,
  IconButton,
  LoadingSpinner,
  PaginationWrapper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderActions,
  TableRow,
} from '../Addresses.styles';

const DistrictPage = ({ onEdit, onDelete, allCountries }) => {
  const dispatch = useDispatch();
  const [regionsForDistricts, setRegionsForDistricts] = useState([]);
  const [citiesForDistricts, setCitiesForDistricts] = useState([]);

  const districts = useSelector(selectAdministrativeDistricts);
  const districtsLoading = useSelector(selectAdministrativeDistrictsLoading);
  const districtsCurrentPage = useSelector(selectAdministrativeDistrictsCurrentPage);
  const districtsPagesCount = useSelector(selectAdministrativeDistrictsPagesCount);
  const districtsSearchData = useSelector(selectAdministrativeDistrictsSearchData);
  const districtsCreateSuccess = useSelector(selectAdministrativeDistrictsSuccess);
  const districtsUpdateSuccess = useSelector(selectAdministrativeDistrictsUpdateSuccess);
  const districtsDeleteSuccess = useSelector(selectAdministrativeDistrictsDeleteSuccess);
  const districtSelectedCountryId = useSelector(
    selectAdministrativeDistrictsSelectedCountryId
  );
  const districtSelectedRegionId = useSelector(selectAdministrativeDistrictsSelectedRegionId);
  const districtSelectedCityId = useSelector(selectAdministrativeDistrictsSelectedCityId);

  useEffect(() => {
    if (districtSelectedCountryId) {
      dispatch(
        getAdministrativeRegions({
          countryId: districtSelectedCountryId,
          limit: 1000,
        })
      ).then((result) => {
        if (result.payload && result.payload.data) {
          setRegionsForDistricts(result.payload.data);
        }
      });
    }
  }, [dispatch, districtSelectedCountryId]);

  useEffect(() => {
    if (districtSelectedCountryId && districtSelectedRegionId) {
      dispatch(
        getCities({
          countryId: districtSelectedCountryId,
          districtId: districtSelectedRegionId,
          limit: 1000,
        })
      ).then((result) => {
        if (result.payload && result.payload.data) {
          setCitiesForDistricts(result.payload.data);
        }
      });
    }
  }, [dispatch, districtSelectedCountryId, districtSelectedRegionId]);

  useEffect(() => {
    if (districtSelectedCountryId && districtSelectedRegionId && districtSelectedCityId) {
      dispatch(
        getAdministrativeDistricts({
          countryId: districtSelectedCountryId,
          districtId: districtSelectedRegionId,
          cityId: districtSelectedCityId,
          page: districtsCurrentPage,
          limit: districtsSearchData.limit,
          search: districtsSearchData.search,
        })
      );
    }
  }, [
    dispatch,
    districtSelectedCountryId,
    districtSelectedRegionId,
    districtSelectedCityId,
    districtsCurrentPage,
    districtsSearchData.limit,
    districtsSearchData.search,
  ]);

  useEffect(() => {
    if (districtsCreateSuccess) {
      dispatch(setAdministrativeDistrictsCreateSuccess(false));
      if (districtSelectedCountryId && districtSelectedRegionId && districtSelectedCityId) {
        dispatch(
          getAdministrativeDistricts({
            countryId: districtSelectedCountryId,
            districtId: districtSelectedRegionId,
            cityId: districtSelectedCityId,
            page: districtsCurrentPage,
            limit: districtsSearchData.limit,
            search: districtsSearchData.search,
          })
        );
      }
    }
  }, [
    districtsCreateSuccess,
    dispatch,
    districtSelectedCountryId,
    districtSelectedRegionId,
    districtSelectedCityId,
    districtsCurrentPage,
    districtsSearchData.limit,
    districtsSearchData.search,
  ]);

  useEffect(() => {
    if (districtsUpdateSuccess) {
      dispatch(setAdministrativeDistrictsUpdateSuccess(false));
    }
  }, [districtsUpdateSuccess, dispatch]);

  useEffect(() => {
    if (districtsDeleteSuccess) {
      dispatch(setAdministrativeDistrictsDeleteSuccess(false));
      if (districts?.length === 1 && districtsCurrentPage > 1) {
        dispatch(setAdministrativeDistrictsCurrentPage(districtsCurrentPage - 1));
      } else {
        // Refetch current page data after deletion
        if (districtSelectedCountryId && districtSelectedRegionId && districtSelectedCityId) {
          dispatch(
            getAdministrativeDistricts({
              countryId: districtSelectedCountryId,
              districtId: districtSelectedRegionId,
              cityId: districtSelectedCityId,
              page: districtsCurrentPage,
              limit: districtsSearchData.limit,
              search: districtsSearchData.search,
            })
          );
        }
      }
    }
  }, [
    districtsDeleteSuccess,
    dispatch,
    districts,
    districtsCurrentPage,
    districtSelectedCountryId,
    districtSelectedRegionId,
    districtSelectedCityId,
    districtsSearchData.limit,
    districtsSearchData.search,
  ]);

  const handleDistrictCountrySelect = (selectedOption) => {
    const countryId = selectedOption ? selectedOption.value : null;
    dispatch(setAdministrativeDistrictsSelectedCountry(countryId));
  };

  const handleDistrictRegionSelect = (selectedOption) => {
    const regionId = selectedOption ? selectedOption.value : null;
    dispatch(setAdministrativeDistrictsSelectedRegion(regionId));
  };

  const handleDistrictCitySelect = (selectedOption) => {
    const cityId = selectedOption ? selectedOption.value : null;
    dispatch(setAdministrativeDistrictsSelectedCity(cityId));
  };

  const handlePageChange = (page) => {
    dispatch(setAdministrativeDistrictsCurrentPage(page));
  };

  const handleRowCountChange = (count) => {
    dispatch(setAdministrativeDistrictsSearchData({ limit: count, offset: 0 }));
    dispatch(setAdministrativeDistrictsCurrentPage(1));
  };

  const districtCountryOptions = allCountries.map((country) => ({
    value: country.id,
    label: country.name,
  }));

  const districtRegionOptions = regionsForDistricts.map((region) => ({
    value: region.id,
    label: region.name,
  }));

  const districtCityOptions = citiesForDistricts.map((city) => ({
    value: city.id,
    label: city.name,
  }));

  const districtSelectedCountryOption = districtSelectedCountryId
    ? districtCountryOptions.find((option) => option.value === districtSelectedCountryId)
    : null;

  const districtSelectedRegionOption = districtSelectedRegionId
    ? districtRegionOptions.find((option) => option.value === districtSelectedRegionId)
    : null;

  const districtSelectedCityOption = districtSelectedCityId
    ? districtCityOptions.find((option) => option.value === districtSelectedCityId)
    : null;

  return (
    <>
      <FilterFlex>
        <FilterSection $noBorder>
          <Select
            label="Select Country"
            value={districtSelectedCountryOption}
            onChange={handleDistrictCountrySelect}
            options={districtCountryOptions}
            placeholder="Select a country"
            isClearable
            isDisabled={allCountries.length === 0}
          />
        </FilterSection>
        <FilterSection $noBorder>
          <Select
            label="Select Region"
            value={districtSelectedRegionOption}
            onChange={handleDistrictRegionSelect}
            options={districtRegionOptions}
            placeholder="Select a region"
            isClearable
            isDisabled={!districtSelectedCountryId || regionsForDistricts.length === 0}
          />
        </FilterSection>
        <FilterSection>
          <Select
            label="Select City / Village"
            value={districtSelectedCityOption}
            onChange={handleDistrictCitySelect}
            options={districtCityOptions}
            placeholder="Select a city/village"
            isClearable
            isDisabled={!districtSelectedRegionId || citiesForDistricts.length === 0}
          />
        </FilterSection>
      </FilterFlex>

      {districtsLoading ? (
        <LoadingSpinner>Loading...</LoadingSpinner>
      ) : !districtSelectedCountryId ? (
        <EmptyState>Please select a country to view administrative districts.</EmptyState>
      ) : !districtSelectedRegionId ? (
        <EmptyState>Please select a region to view administrative districts.</EmptyState>
      ) : !districtSelectedCityId ? (
        <EmptyState>Please select a city/village to view administrative districts.</EmptyState>
      ) : !districts || districts.length === 0 ? (
        <EmptyState>No administrative districts found.</EmptyState>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                <TableHeaderActions>Actions</TableHeaderActions>
              </TableRow>
            </TableHead>
            <TableBody>
              {districts.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <ActionsCell>
                    <IconButton onClick={() => onEdit(item)}>
                      <EditIcon size={18} color="#2D6CDF" />
                    </IconButton>
                    <IconButton onClick={() => onDelete(item)}>
                      <DeleteIcon size={18} color="#FB2C36" />
                    </IconButton>
                  </ActionsCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {districtsPagesCount > 1 && (
            <PaginationWrapper>
              <Pagination
                currentPage={districtsCurrentPage}
                totalPages={districtsPagesCount}
                onPageChange={handlePageChange}
                handleRowCountChange={handleRowCountChange}
                dataCount={districts.length}
                count={districtsSearchData.limit}
                editableRowCount
              />
            </PaginationWrapper>
          )}
        </>
      )}
    </>
  );
};

export default DistrictPage;
